import React, { useState, useEffect, Suspense, lazy } from 'react'
import { Link, Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { usePermissions } from '@/hooks/usePermissions'
import OliAssistant from '@/components/assistant/OliAssistant'
import GlobalSearchModal from '@/components/search/GlobalSearchModal'
import OliAiIcon from '@/components/OliAiIcon'
import NotificationToastListener from '@/components/notifications/NotificationToastListener'
import { useNotifications } from '@/hooks/useApi'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api'
import { useIsMobile } from '@/hooks/use-mobile'
import {
  LayoutDashboard,
  Users,
  CheckSquare,
  Calendar as CalendarIcon,
  Bell,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  Search,
  FileText,
  Target,
  MessageSquare,
  Home,
  Briefcase,
  UserCircle,
  Send,
  Wrench,
  Mail,
  Share2,
  UserPlus,
  CreditCard,
  TrendingUp,
  BarChart3,
  Folder,
  Shield,
  Activity,
  ShoppingBag,
  UserCog,
  HelpCircle,
  Clock,
  Lock,
  Globe,
  MapPin,
  Link as LinkIcon,
  Building2,
  X,
  MailPlus,
  MessageSquarePlus,
  PenSquare,
  Tag,
  Zap,
  Inbox as InboxIcon,
} from 'lucide-react'
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarHeader,
  SidebarFooter,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar'
import { Badge } from '@/components/ui/badge'
import { Toaster } from '@/components/ui/sonner'

import { VESTOLIO_FULL_LOGO, VESTOLIO_ICON_LOGO } from '@/lib/branding'
import { useComposeListener } from '@/lib/composeEvents'
import OliBrandIcon from "@/components/OliBrandIcon";

/**
 * Normalize a file URL: strip absolute localhost origins so CSP "self" is satisfied.
 * Converts "http://localhost:4000/uploads/foo.png" → "/uploads/foo.png"
 * Leaves external URLs (https://...) and already-relative paths untouched.
 */
function normalizeFileUrl(url?: string): string {
  if (!url) return ''
  try {
    const u = new URL(url, window.location.origin)
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
      return u.pathname  // strip origin, keep path
    }
  } catch { /* not a valid URL, return as-is */ }
  return url
}

// ─── Sidebar Header ─────────────────────────────────────────
function SidebarHeaderContent({ navigate, brandingSettings }: { navigate: (path: string) => void; brandingSettings?: any }) {
  const { state } = useSidebar()
  const isMobile = useIsMobile()
  const isCollapsed = !isMobile && state === 'collapsed'
  const whiteLabel = brandingSettings?.setting_value
  const companyLogo = normalizeFileUrl(whiteLabel?.company_logo)
  const companyIconLogo = normalizeFileUrl(whiteLabel?.company_icon_logo)
  const hasWhiteLabel = !!(companyLogo || companyIconLogo)

  return (
    <SidebarHeader className="border-b border-slate-200 h-[52px] flex items-center justify-center px-3 group-data-[collapsible=icon]/sidebar:px-2">
      {hasWhiteLabel ? (
        // White label logo
        <div className="flex items-center justify-center w-full">
          {isCollapsed ? (
            // Collapsed: show icon logo or first letter fallback
            companyIconLogo ? (
              <img
                src={companyIconLogo}
                alt={whiteLabel?.company_name || 'Company'}
                className="h-8 w-8 object-contain cursor-pointer rounded"
                onClick={() => navigate('/')}
              />
            ) : (
              <div
                className="h-8 w-8 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm cursor-pointer"
                onClick={() => navigate('/')}
              >
                {(whiteLabel?.company_name || 'C')[0].toUpperCase()}
              </div>
            )
          ) : (
            // Expanded: show full logo
            <img
              src={companyLogo || companyIconLogo}
              alt={whiteLabel?.company_name || 'Company'}
              className="h-7 w-auto max-w-[180px] object-contain cursor-pointer"
              onClick={() => navigate('/')}
            />
          )}
        </div>
      ) : (
        // No white label — show Vestolio logo
        <div className="flex items-center justify-center w-full">
          {isCollapsed ? (
            <div className="w-8 h-8 overflow-hidden flex items-center justify-center rounded-lg cursor-pointer" onClick={() => navigate('/')}>
              <img
                src={VESTOLIO_FULL_LOGO}
                alt="Vestolio"
                className="h-7 w-auto max-w-none"
                style={{ clipPath: 'inset(0 65% 0 0)', transform: 'scale(2.5)', transformOrigin: 'left center' }}
              />
            </div>
          ) : (
            <img
              src={VESTOLIO_FULL_LOGO}
              alt="Vestolio"
              className="h-7 w-auto cursor-pointer"
              onClick={() => navigate('/')}
            />
          )}
        </div>
      )}
    </SidebarHeader>
  )
}

// ─── Sidebar Footer (Powered by) ────────────────────────────
function SidebarFooterContent({ brandingSettings }: { brandingSettings?: any }) {
  const { state } = useSidebar()
  const isMobile = useIsMobile()
  const isCollapsed = !isMobile && state === 'collapsed'
  const footerLogo = normalizeFileUrl(brandingSettings?.setting_value?.company_logo)
  const footerIcon = normalizeFileUrl(brandingSettings?.setting_value?.company_icon_logo)
  const hasWhiteLabel = !!(footerLogo || footerIcon)

  if (!hasWhiteLabel) return null

  return (
    <SidebarFooter className="border-t border-slate-200 py-2 px-3 group-data-[collapsible=icon]/sidebar:px-1">
      <div className="flex items-center justify-center gap-1.5">
        {isCollapsed ? (
          <img
            src={VESTOLIO_ICON_LOGO}
            alt="Vestolio"
            className="h-6 w-6 object-contain opacity-70 transition-opacity"
            title="Powered by Vestolio"
          />
        ) : (
          <div className="flex items-center gap-1.5 opacity-70 transition-opacity">
            <span className="text-[10px] text-slate-400 font-medium">Powered by</span>
            <img
              src={VESTOLIO_FULL_LOGO}
              alt="Vestolio"
              className="h-3.5 w-auto"
            />
          </div>
        )}
      </div>
    </SidebarFooter>
  )
}

// ─── Sidebar Navigation ──────────────────────────────────────
function SidebarNav({
  navigationGroups,
  location,
}: {
  navigationGroups: any[]
  location: any
}) {
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  const [hoveredGroupIdx, setHoveredGroupIdx] = useState<number | null>(null)
  const [hoverPosition, setHoverPosition] = useState({ top: 0, left: 0 })
  const closeTimeoutRef = React.useRef<any>(null)
  const { can, isAdmin } = usePermissions()

  // Filter items based on permissions
  const filteredGroups = React.useMemo(() => {
    return navigationGroups
      .map((group: any) => ({
        ...group,
        items: group.items.filter((item: any) => {
          if (!item) return false
          // Admin-only items
          if (item.adminOnly) return isAdmin
          // Permission-gated items
          if (item.permission) {
            return can(item.permission[0], item.permission[1])
          }
          // No restriction
          return true
        }),
      }))
      .filter((group: any) => group.items.length > 0)
  }, [navigationGroups, can, isAdmin])

  const handleGroupHover = (idx: number, event: React.MouseEvent) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
    if (event?.currentTarget) {
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
      setHoverPosition({ top: rect.top, left: rect.right + 8 })
    }
    setHoveredGroupIdx(idx)
  }

  const handleGroupLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredGroupIdx(null)
      closeTimeoutRef.current = null
    }, 150)
  }

  const handleCardEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  const handleCardLeave = () => {
    setHoveredGroupIdx(null)
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current)
      closeTimeoutRef.current = null
    }
  }

  return (
    <>
      <SidebarContent className="p-3 group-data-[collapsible=icon]/sidebar:p-1 overflow-y-auto">
        {filteredGroups.map((group: any, idx: number) => {
          if (!group || !group.items) return null

          const GroupIcon = group.icon
          const hasActiveChild = group.items.some(
            (item: any) => item && location.pathname === item.url
          )

          return (
            <SidebarGroup
              key={idx}
              className="group-data-[collapsible=icon]/sidebar:p-0.5 relative"
            >
              {/* EXPANDED MODE */}
              {!isCollapsed && (
                <button
                  onClick={() => group.setIsOpen(!group.isOpen)}
                  className="w-full"
                >
                  <SidebarGroupLabel className="text-xs font-semibold text-slate-700 uppercase tracking-wider px-3 py-2 flex items-center justify-between hover:text-blue-900 cursor-pointer">
                    <span>{group.label}</span>
                    {group.isOpen ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </SidebarGroupLabel>
                </button>
              )}

              {/* COLLAPSED MODE */}
              {isCollapsed && (
                <SidebarMenu>
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => group.setIsOpen(!group.isOpen)}
                      onMouseEnter={(e) => handleGroupHover(idx, e)}
                      onMouseLeave={handleGroupLeave}
                      className={`hover:bg-blue-50 hover:text-blue-900 transition-all duration-200 rounded-xl mb-0.5 cursor-pointer ${
                        hasActiveChild
                          ? 'bg-gradient-to-r from-blue-900 to-indigo-900 text-white shadow-lg'
                          : group.isOpen
                            ? 'bg-slate-100 text-slate-900'
                            : ''
                      }`}
                    >
                      <GroupIcon className="w-4 h-4 shrink-0" />
                      <span className="font-medium text-sm">{group.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                </SidebarMenu>
              )}

              {/* Menu items */}
              {!isCollapsed && group.isOpen && (
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.items.map((item: any) => {
                      if (!item || !item.icon) return null
                      const ItemIcon = item.icon
                      const isActive = location.pathname === item.url

                      return (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton
                            asChild
                            tooltip={item.title}
                            className={`group/navitem transition-all duration-200 rounded-xl mb-0.5 ${
                              isActive
                                ? 'bg-gradient-to-r from-blue-900 to-indigo-900 text-white shadow-lg hover:from-blue-800 hover:to-indigo-800'
                                : 'text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            <Link
                              to={item.url}
                              className="flex items-center gap-3 px-4 py-2.5"
                            >
                              {ItemIcon === OliBrandIcon ? (
                                <ItemIcon className="w-4 h-4 shrink-0" variant={isActive ? 'white' : 'color'} />
                              ) : (
                                <ItemIcon className={`w-4 h-4 shrink-0 ${isActive ? 'text-white' : 'text-slate-500 group-hover/navitem:text-slate-700'}`} />
                              )}
                              <span className={`font-medium text-sm ${isActive ? 'text-white' : 'text-slate-500 group-hover/navitem:text-slate-700'}`}>{item.title}</span>
                              {item.badge > 0 && !isCollapsed && (
                                <Badge
                                  variant="default"
                                  className="ml-auto bg-red-500 hover:bg-red-500 text-white text-xs h-5 min-w-[20px] flex items-center justify-center"
                                >
                                  {item.badge}
                                </Badge>
                              )}
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      )
                    })}
                  </SidebarMenu>
                </SidebarGroupContent>
              )}
            </SidebarGroup>
          )
        })}
      </SidebarContent>

      {/* Hover Card for collapsed sidebar */}
      {hoveredGroupIdx !== null &&
        isCollapsed &&
        filteredGroups[hoveredGroupIdx] && (
          <div
            className="fixed bg-white border border-slate-200 rounded-lg shadow-2xl z-[9999] overflow-hidden w-48"
            style={{
              top: `${hoverPosition.top}px`,
              left: `${hoverPosition.left}px`,
            }}
            onMouseEnter={handleCardEnter}
            onMouseLeave={handleCardLeave}
          >
            {(() => {
              const group = filteredGroups[hoveredGroupIdx]
              const GroupIcon = group.icon
              return (
                <>
                  <div className="border-b border-slate-200 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <GroupIcon className="w-4 h-4 text-blue-600" />
                      <h4 className="font-semibold text-slate-900 text-sm">{group.label}</h4>
                    </div>
                  </div>
                  <div className="p-2 pointer-events-auto">
                    {group.items.map((item: any) => {
                      if (!item || !item.icon) return null
                      const ItemIcon = item.icon
                      const isActive = location.pathname === item.url
                      return (
                        <Link
                          key={item.title}
                          to={item.url}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md text-xs transition-colors cursor-pointer block ${
                            isActive
                              ? 'bg-gradient-to-r from-blue-900 to-indigo-900 text-white shadow-lg font-medium'
                              : 'hover:bg-slate-100 text-slate-700'
                          }`}
                        >
                          {ItemIcon === OliBrandIcon ? (
                            <ItemIcon className="w-3.5 h-3.5 shrink-0" variant={isActive ? 'white' : 'color'} />
                          ) : (
                            <ItemIcon className="w-3.5 h-3.5 shrink-0" />
                          )}
                          <span className="truncate">{item.title}</span>
                          {item.badge > 0 && (
                            <span className="ml-auto text-xs font-semibold bg-red-500 text-white h-5 min-w-[20px] flex items-center justify-center rounded">
                              {item.badge}
                            </span>
                          )}
                        </Link>
                      )
                    })}
                  </div>
                </>
              )
            })()}
          </div>
        )}
    </>
  )
}

// ─── Top Header ──────────────────────────────────────────────
function TopHeader({
  user,
  navigate,
  unreadNotifications,
  userRole,
  brandingSettings,
  isCommunicationsOpen,
  setIsCommunicationsOpen,
}: {
  user: any
  navigate: (path: string) => void
  unreadNotifications: any[]
  userRole: string
  brandingSettings?: any
  isCommunicationsOpen: boolean
  setIsCommunicationsOpen: (v: boolean) => void
}) {
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const [isSettingsOpen, setIsSettingsOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)

  // Unread messages count
  const { data: unreadMessages = [] } = useQuery({
    queryKey: ['unread-messages-nav'],
    queryFn: () => api.entities.ClientAdvisorMessage.filter({ is_read: false, sender: 'client' }),
    staleTime: 120000,
    refetchInterval: 120000,
    refetchOnWindowFocus: false,
  })

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setIsSearchOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])
  const isAdminUser = ['super_admin', 'admin'].includes(userRole)
  const { can, canAny } = usePermissions()
  const { logout } = useAuth()

  const [expandedSettings, setExpandedSettings] = useState<Record<string, boolean>>({
    system: false,
    integrations: false,
    scoring: false,
    investments: false,
    dataManagement: false,
    templates: false,
    teamRoles: false,
    maintenance: false,
  })

  const toggleSection = (section: string) => {
    setExpandedSettings((prev) => ({
      ...prev,
      [section]: !prev[section],
    }))
  }

  const handleSettingsNavigate = (path: string, tab: string | null = null) => {
    if (tab) {
      navigate(`${path}?tab=${tab}`)
    } else {
      navigate(path)
    }
    setIsSettingsOpen(false)
  }

  return (
    <>
    <header className="fixed top-0 right-0 left-0 bg-white border-b border-slate-200 px-2 sm:px-4 h-[52px] flex items-center justify-between gap-2 sm:gap-4 shrink-0 z-50">
      {/* Left */}
      <div className="flex items-center gap-0 md:gap-2">
        <SidebarTrigger className="hover:bg-slate-100 transition-colors rounded-lg p-1.5 h-10 w-10 flex items-center justify-center">
          <ChevronsLeft className="h-5 w-5 text-slate-600" />
        </SidebarTrigger>
        {(() => {
          const wl = brandingSettings?.setting_value
          const wlLogo = normalizeFileUrl(wl?.company_logo)
          const wlIcon = normalizeFileUrl(wl?.company_icon_logo)
          const hasWL = !!(wlLogo || wlIcon)
          return (
            <>
              <div className="hidden md:flex items-center gap-4">
                <div className="w-px h-6 bg-slate-200" />
                <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                  {hasWL ? (
                    <img
                      src={wlLogo || wlIcon}
                      alt={wl?.company_name || 'Company'}
                      className="h-7 w-auto max-w-[160px] object-contain"
                    />
                  ) : (
                    <img
                      src={VESTOLIO_FULL_LOGO}
                      alt="Vestolio"
                      className="h-7 w-auto"
                    />
                  )}
                </div>
              </div>
              <div className="flex md:hidden items-center justify-center gap-2">
                <div className="w-px h-6 bg-slate-200" />
                <div className="flex items-center gap-1.5 cursor-pointer" onClick={() => navigate('/')}>
                  {hasWL ? (
                    <img
                      src={wlIcon || wlLogo}
                      alt={wl?.company_name || 'Company'}
                      className="h-7 w-auto max-w-[140px] object-contain"
                    />
                  ) : (
                    <img
                      src={VESTOLIO_FULL_LOGO}
                      alt="Vestolio"
                      className="h-5 w-auto"
                    />
                  )}
                </div>
              </div>
            </>
          )
        })()}
      </div>

      {/* Center - Search */}
      <div className="flex-1 max-w-md mx-auto hidden sm:block">
        <button onClick={() => setIsSearchOpen(true)} className="w-full flex items-center gap-2 px-3 py-1.5 text-sm bg-slate-100/80 rounded-lg hover:bg-slate-200/80 transition-all text-slate-500 relative group cursor-pointer">
          <Search className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-500 transition-colors shrink-0" />
          <span className="text-slate-400 group-hover:text-slate-500 text-xs font-medium transition-colors">Search clients, tasks, goals...</span>
          <kbd className="ml-auto text-[10px] bg-white text-slate-400 px-1.5 py-0.5 rounded font-mono border border-slate-200/80 shadow-sm hidden md:inline-flex items-center gap-0.5">⌘K</kbd>
        </button>
      </div>

      <button onClick={() => setIsSearchOpen(true)} className="sm:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-500">
        <Search className="h-4 w-4" />
      </button>

      <GlobalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      {/* Right */}
      <div className="flex items-center gap-1 relative" onClick={(e) => e.stopPropagation()}>
        {/* Notifications */}
        <button
          onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
          className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-600 hover:text-slate-900"
          title="Notifications"
        >
          <Bell className="h-4 w-4" />
          {(unreadNotifications?.length || 0) > 0 && (
            <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
          )}
        </button>

        {/* Notifications Dropdown */}
        {isNotificationsOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsNotificationsOpen(false)} />
            <div className="absolute top-full right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] sm:w-96 bg-white rounded-lg shadow-2xl z-50 max-h-[70vh] sm:max-h-[500px] flex flex-col border border-slate-200">
              <div className="border-b border-slate-200 px-5 py-4 flex items-center justify-between shrink-0 bg-gradient-to-r from-blue-50 to-slate-50">
                <h3 className="font-semibold text-slate-900 text-base">Notifications</h3>
                {unreadNotifications.length > 0 && (
                  <span className="text-xs font-medium text-slate-600">
                    {unreadNotifications.length} new
                  </span>
                )}
              </div>
              <div className="overflow-y-auto flex-1">
                {unreadNotifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                    <Bell className="h-12 w-12 text-slate-200 mb-3" />
                    <p className="text-slate-600 text-sm font-medium">No new notifications yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-200">
                    {unreadNotifications.slice(0, 5).map((n: any, idx: number) => (
                      <div
                        key={idx}
                        className="px-5 py-4 hover:bg-blue-50 transition-colors cursor-pointer border-l-2 border-l-transparent hover:border-l-blue-500"
                      >
                        <p className="font-semibold text-slate-900 text-sm leading-tight">
                          {n.title || n.message || 'Notification'}
                        </p>
                        {n.message && n.title && (
                          <p className="text-slate-600 text-xs mt-2 leading-relaxed">{n.message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="border-t border-slate-200 px-5 py-3 shrink-0 bg-slate-50">
                <button
                  onClick={() => { navigate('/inbox?view=notifications'); setIsNotificationsOpen(false) }}
                  className="w-full text-center text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                >
                  View All
                </button>
              </div>
            </div>
          </>
        )}

        {/* Communications */}
        <div className="relative">
          <button
            onClick={() => setIsCommunicationsOpen(!isCommunicationsOpen)}
            className={`relative p-2 rounded-lg transition-colors ${isCommunicationsOpen ? 'bg-blue-100 text-blue-700' : 'hover:bg-slate-100 text-slate-600 hover:text-slate-900'}`}
            title="Communications"
          >
            <MessageSquare className="h-4 w-4" />
            {unreadMessages.length > 0 && (
              <span className="absolute top-1 right-1 min-w-[0.5rem] h-2 bg-red-500 rounded-full" />
            )}
          </button>
        </div>

        {/* Settings */}
        {canAny(['settings', 'system'], ['settings', 'integrations'], ['settings', 'scoring'], ['settings', 'investments'], ['settings', 'data_management'], ['settings', 'templates'], ['settings', 'team_roles'], ['settings', 'maintenance']) && (
        <div className="relative">
          <button
            onClick={() => setIsSettingsOpen(!isSettingsOpen)}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-600 hover:text-slate-900 hidden sm:flex"
            title="Settings"
          >
            <Settings className="h-4 w-4" />
          </button>
          {isSettingsOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsSettingsOpen(false)}
                style={{ pointerEvents: 'auto' }}
              />
              <div className="fixed top-[52px] right-1 sm:right-4 w-72 max-w-[calc(100vw-1rem)] sm:w-80 bg-white rounded-lg shadow-2xl z-50 border border-slate-200 max-h-[70vh] sm:max-h-[calc(100vh-100px)] overflow-y-auto">
                {/* Header */}
                <div className="border-b border-slate-200 px-5 py-4 sticky top-0 bg-white bg-gradient-to-r from-blue-50 to-slate-50 z-10">
                  <h3 className="font-semibold text-slate-900 text-base">Settings</h3>
                  <p className="text-xs text-slate-600 mt-1">Manage your system</p>
                </div>

                {/* Menu Items — Alphabetical order */}
                <div className="py-2" style={{ pointerEvents: 'auto' }}>
                  {/* DATA MANAGEMENT - settings.data_management permission */}
                  {can('settings', 'data_management') && (
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSection('dataManagement') }}
                      className="w-full px-5 py-3 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-blue-500 cursor-pointer"
                    >
                      <FileText className="h-4 w-4 shrink-0 text-purple-600" />
                      <span className="font-medium">DATA MANAGEMENT</span>
                      <ChevronDown className={`h-4 w-4 ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${expandedSettings.dataManagement ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {expandedSettings.dataManagement && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/segments') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Tag className="h-4 w-4 shrink-0 text-indigo-500" />
                          <span>Segments</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/clientsettings') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Activity className="h-4 w-4 shrink-0 text-blue-500" />
                          <span>Operational Workflow</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'custom-fields') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Settings className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Custom Fields</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'tags') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Target className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Data Tags</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'document-folders') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Folder className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Document Management</span>
                        </button>
                      </>
                    )}
                  </div>
                  )}

                  {/* INTEGRATIONS - settings.integrations permission */}
                  {can('integrations', 'view') && can('settings', 'integrations') && (
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSection('integrations') }}
                      className="w-full px-5 py-3 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-blue-500 cursor-pointer"
                    >
                      <LinkIcon className="h-4 w-4 shrink-0 text-purple-600" />
                      <span className="font-medium">INTEGRATIONS</span>
                      <ChevronDown className={`h-4 w-4 ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${expandedSettings.integrations ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {expandedSettings.integrations && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'integrations-calendar') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <CalendarIcon className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Calendar</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'integrations-communications') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <MessageSquare className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Communications</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'integrations-custodians') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Briefcase className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Investment Custodians</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'integrations-operations') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Wrench className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Operations</span>
                        </button>
                      </>
                    )}
                  </div>
                  )}

                  {/* INVESTMENTS - settings.investments permission */}
                  {can('settings', 'investments') && (
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSection('investments') }}
                      className="w-full px-5 py-3 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-blue-500 cursor-pointer"
                    >
                      <CreditCard className="h-4 w-4 shrink-0 text-emerald-600" />
                      <span className="font-medium">INVESTMENTS</span>
                      <ChevronDown className={`h-4 w-4 ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${expandedSettings.investments ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {expandedSettings.investments && (
                      <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'investment-models') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                        <Clock className="h-4 w-4 shrink-0 text-slate-400" />
                        <span>Investment Models</span>
                      </button>
                    )}
                  </div>
                  )}

                  {/* MAINTENANCE - settings.maintenance permission */}
                  {can('settings', 'maintenance') && (
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSection('maintenance') }}
                      className="w-full px-5 py-3 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-blue-500 cursor-pointer"
                    >
                      <Wrench className="h-4 w-4 shrink-0 text-slate-600" />
                      <span className="font-medium">MAINTENANCE</span>
                      <ChevronDown className={`h-4 w-4 ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${expandedSettings.maintenance ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {expandedSettings.maintenance && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/changelog') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Change Log</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/featuretesting') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Activity className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Feature Testing</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/tenantmanagement') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Building2 className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Organization Management</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'reference') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <HelpCircle className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>System Reference</span>
                        </button>
                      </>
                    )}
                  </div>
                  )}

                  {/* SCORING & ANALYTICS - settings.scoring permission */}
                  {can('settings', 'scoring') && (
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSection('scoring') }}
                      className="w-full px-5 py-3 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-blue-500 cursor-pointer"
                    >
                      <TrendingUp className="h-4 w-4 shrink-0 text-blue-600" />
                      <span className="font-medium">SCORING & ANALYTICS</span>
                      <ChevronDown className={`h-4 w-4 ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${expandedSettings.scoring ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {expandedSettings.scoring && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'benchmarks') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <BarChart3 className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Cash Flow Benchmarks</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/financialscoringsettings') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <TrendingUp className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Financial Scoring</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/scoringmetricsregistry') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Target className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Scoring Metrics Registry</span>
                        </button>
                      </>
                    )}
                  </div>
                  )}

                  {/* SYSTEM - settings.system permission */}
                  {can('settings', 'system') && (
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSection('system') }}
                      className="w-full px-5 py-3 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-blue-500 cursor-pointer"
                    >
                      <UserCog className="h-4 w-4 shrink-0 text-blue-600" />
                      <span className="font-medium">SYSTEM</span>
                      <ChevronDown className={`h-4 w-4 ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${expandedSettings.system ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {expandedSettings.system && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'branding') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Folder className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Branding & White Label</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/globalsettings', 'general') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Globe className="h-4 w-4 shrink-0 text-blue-600" />
                          <span>General Settings</span>
                        </button>
                      </>
                    )}
                  </div>
                  )}

                  {/* TEAM & ROLES - settings.team_roles permission */}
                  {can('settings', 'team_roles') && canAny(['users', 'view'], ['teams', 'view']) && (
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSection('teamRoles') }}
                      className="w-full px-5 py-3 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-blue-500 cursor-pointer"
                    >
                      <Users className="h-4 w-4 shrink-0 text-amber-500" />
                      <span className="font-medium">TEAM & ROLES</span>
                      <ChevronDown className={`h-4 w-4 ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${expandedSettings.teamRoles ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {expandedSettings.teamRoles && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/advisorstructure') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Users className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Advisor Structure</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/locationmanagement') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <MapPin className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Location Management</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/org-hierarchy') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Users className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Org Hierarchy</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/rolemanagement') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Lock className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Roles & Permissions</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/teammanagement') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Users className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Team Management</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/usermanagement') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Users className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>User Management</span>
                        </button>
                      </>
                    )}
                  </div>
                  )}

                  {/* TEMPLATES - settings.templates permission */}
                  {can('templates', 'view') && can('settings', 'templates') && (
                  <div>
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleSection('templates') }}
                      className="w-full px-5 py-3 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-blue-500 cursor-pointer"
                    >
                      <Folder className="h-4 w-4 shrink-0 text-blue-600" />
                      <span className="font-medium">TEMPLATES</span>
                      <ChevronDown className={`h-4 w-4 ml-auto shrink-0 text-slate-400 transition-transform duration-200 ${expandedSettings.templates ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {expandedSettings.templates && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/marketingcampaigns') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Mail className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Campaign Templates</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/goaltemplates') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Target className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Goal Templates</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/reviewtemplates') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Review Templates</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/tasktemplates') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <CheckSquare className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Task Templates</span>
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleSettingsNavigate('/internalworkflows') }} className="w-full px-5 py-2.5 pl-11 text-left text-sm text-slate-600 hover:bg-slate-50 hover:text-blue-900 transition-colors flex items-center gap-2 border-l-2 border-l-transparent hover:border-l-blue-500">
                          <Wrench className="h-4 w-4 shrink-0 text-slate-400" />
                          <span>Workflow Templates</span>
                        </button>
                      </>
                    )}
                  </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        )}

        {/* Profile */}
        <div className="relative">
          <button
            onClick={() => setIsProfileOpen(!isProfileOpen)}
            className="ml-1 w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold text-xs hover:opacity-90 transition-opacity"
            title={user?.name || 'Profile'}
          >
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </button>
          {isProfileOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setIsProfileOpen(false)}
                style={{ pointerEvents: 'auto' }}
              />
              <div className="fixed top-[52px] right-1 sm:right-4 w-64 max-w-[calc(100vw-1rem)] sm:w-80 bg-white rounded-lg shadow-2xl z-50 border border-slate-200 max-h-[70vh] sm:max-h-[calc(100vh-100px)] overflow-y-auto">
                {/* User Profile Header */}
                <div className="border-b border-slate-200 px-4 py-4 bg-gradient-to-r from-blue-50 to-slate-50">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                      {user?.name?.[0]?.toUpperCase() || 'A'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 text-sm truncate">{user?.name || 'User'}</p>
                      <p className="text-xs text-slate-600 mt-0.5 truncate">{user?.email || ''}</p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-1" style={{ pointerEvents: 'auto' }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      navigate('/usersettings')
                      setIsProfileOpen(false)
                    }}
                    className="w-full px-4 py-3 text-left text-sm text-slate-700 hover:bg-blue-50 hover:text-blue-900 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-blue-500 cursor-pointer"
                  >
                    <UserCircle className="h-4 w-4 shrink-0" />
                    <span>Account Settings</span>
                  </button>

                  <div className="border-t border-slate-200 my-1" />

                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      logout()
                      setIsProfileOpen(false)
                    }}
                    className="w-full px-4 py-3 text-left text-sm text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors flex items-center gap-3 border-l-2 border-l-transparent hover:border-l-red-500 cursor-pointer"
                  >
                    <LogOut className="h-4 w-4 shrink-0" />
                    <span>Log out</span>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
    </>
  )
}

const LazyCommunicationComposer = lazy(() => import('@/components/communications/CommunicationComposer'))
const LazyMessageThreadDrawer = lazy(() => import('@/components/messages/MessageThreadDrawer'))
const LazyUniversalDrawer = lazy(() => import('@/components/common/UniversalDrawer'))

// ─── Communications Toolbar ──────────────────────────────────
function CommunicationsToolbar({ navigate, onClose, onCompose }: { navigate: (path: string) => void; onClose: () => void; onCompose: (type: 'email' | 'sms' | 'message') => void }) {
  const { state } = useSidebar()
  const sidebarWidth = state === 'expanded' ? '16rem' : '4rem'

  // Fetch unread counts for each channel
  const { data: unreadInbox = [] } = useQuery({
    queryKey: ['toolbar-unread-messages'],
    queryFn: () => api.entities.ClientAdvisorMessage.filter({ is_read: false, sender: 'client' }),
    staleTime: 60000,
    refetchInterval: 60000,
  })
  const { data: communications = [] } = useQuery({
    queryKey: ['toolbar-communications'],
    queryFn: () => api.entities.Communication.list('-created_date'),
    staleTime: 60000,
    refetchInterval: 60000,
  })

  const unreadEmails = communications.filter((c: any) => c.communication_type === 'email' && (c.status === 'delivered' || c.status === 'sent')).length
  const unreadSms = communications.filter((c: any) => c.communication_type === 'sms' && (c.status === 'delivered' || c.status === 'sent')).length
  const unreadMessages = unreadInbox.length

  const inboxItems = [
    { icon: Send,          label: 'Messages',  path: '/messages',                  count: unreadMessages },
    { icon: Mail,          label: 'Emails',    path: '/communications?type=email', count: unreadEmails },
    { icon: MessageSquare, label: 'SMS',       path: '/communications?type=sms',   count: unreadSms },
  ]

  return (
    <>
    <div className="fixed z-40 bg-white border-b border-slate-200 shadow-sm" style={{ top: '52px', left: sidebarWidth, right: 0, transition: 'left 0.2s' }}>
      <div className="flex items-center justify-end gap-1 px-3 py-1 min-w-0">
        {/* Inbox / Navigate section with badges */}
        {inboxItems.map(({ icon: Icon, label, path, count }) => (
          <button
            key={label}
            onClick={() => { navigate(path); onClose() }}
            className="p-1.5 rounded-md hover:bg-blue-50 text-slate-500 hover:text-blue-600 transition-colors relative group shrink-0"
            title={label}
          >
            <Icon className="h-4 w-4" />
            {count > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold px-1 leading-none">
                {count > 99 ? '99+' : count}
              </span>
            )}
            <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 text-[10px] font-medium text-slate-600 bg-white border border-slate-200 rounded px-1.5 py-0.5 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-sm pointer-events-none z-50">
              {label}
            </span>
          </button>
        ))}

        {/* Divider */}
        <div className="w-px h-4 bg-slate-200 mx-2 shrink-0" />

        {/* Compose section — opens drawer without navigating */}
        {[
          { icon: MailPlus,           label: 'Compose Email',   type: 'email' as const },
          { icon: MessageSquarePlus,   label: 'Compose SMS',     type: 'sms' as const },
          { icon: PenSquare,           label: 'Compose Message', type: 'message' as const },
        ].map(({ icon: Icon, label, type }) => (
          <button
            key={label}
            onClick={() => onCompose(type)}
            className="p-1.5 rounded-md hover:bg-green-50 text-slate-500 hover:text-green-600 transition-colors relative group shrink-0"
            title={label}
          >
            <Icon className="h-4 w-4" />
            <span className="absolute top-full mt-1 left-1/2 -translate-x-1/2 text-[10px] font-medium text-slate-600 bg-white border border-slate-200 rounded px-1.5 py-0.5 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity shadow-sm pointer-events-none z-50">
              {label}
            </span>
          </button>
        ))}

        {/* Close */}
        <div className="w-px h-4 bg-slate-200 mx-1.5 shrink-0" />
        <button
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors shrink-0"
          title="Close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
    </>
  )
}

// ─── Client Layout (lazy loaded) ─────────────────────────────
const ClientLayout = lazy(() =>
  import('./layouts/ClientLayout').catch((err) => {
    const key = 'chunk_reload_' + window.location.pathname
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1')
      window.location.reload()
      return new Promise(() => {}) as any
    }
    throw err
  })
)

// ─── Main Layout ─────────────────────────────────────────────
export default function Layout() {
  const { user, loading } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const isClientUser = user && ['client', 'user'].includes((user.role || '').toLowerCase())
  const userRole = (user?.role || '').toLowerCase()

  // On mobile, start all sections collapsed so all headers are visible.
  // On desktop, auto-expand the section containing the current route.
  const isMobileInit = typeof window !== 'undefined' && window.innerWidth < 768
  const currentPath = location.pathname
  const [isOverviewOpen, setIsOverviewOpen] = useState(!isMobileInit && ['/', '/workload', '/calendar', '/notifications', '/operations', '/tasks', '/cases'].includes(currentPath))
  const [isClientMgmtOpen, setIsClientMgmtOpen] = useState(!isMobileInit && ['/clients', '/households', '/leads', '/actions', '/goals', '/reviews'].some(u => currentPath.startsWith(u)))
  const [isEngagementOpen, setIsEngagementOpen] = useState(!isMobileInit && ['/messages', '/communications', '/marketingcampaigns', '/forms'].some(u => currentPath.startsWith(u)))
  const [isInsightsOpen, setIsInsightsOpen] = useState(!isMobileInit && ['/intelligencecenter', '/reports', '/tools', '/documentsigning', '/compliancecenter', '/marketplace', '/mypurchases', '/marketplaceanalytics'].some(u => currentPath.startsWith(u)))
  const [isOliOpen, setIsOliOpen] = useState(false)
  const [isOliMinimized, setIsOliMinimized] = useState(false)
  const [isCommunicationsOpen, setIsCommunicationsOpen] = useState(false)

  // ── Compose drawer state (always mounted, triggered from toolbar or any page via composeEvents) ──
  const [composeOpen, setComposeOpen] = useState(false)
  const [composeType, setComposeType] = useState<'email' | 'sms'>('email')
  const [composeClient, setComposeClient] = useState<any>(null)
  const [messageDrawerOpen, setMessageDrawerOpen] = useState(false)
  const [messageDrawerClientId, setMessageDrawerClientId] = useState<string | null>(null)
  const [clientSearch, setClientSearch] = useState('')

  const handleCompose = React.useCallback((type: 'email' | 'sms' | 'message', client?: any) => {
    if (type === 'message') {
      setMessageDrawerClientId(client?.clientId || null)
      setClientSearch('')
      setMessageDrawerOpen(true)
    } else {
      setComposeType(type)
      setComposeClient(client ? { id: client.clientId, full_name: client.clientName, email: client.email, phone: client.phone } : null)
      setComposeOpen(true)
    }
  }, [])

  // Listen for compose events from any page (e.g. ClientMessagesHub)
  useComposeListener(React.useCallback((type: any, client?: any) => {
    handleCompose(type, client)
  }, [handleCompose]))

  // Clients list for message compose drawer
  const { data: composeClients = [] } = useQuery({
    queryKey: ['compose-clients'],
    queryFn: () => api.entities.Client.list(),
    enabled: messageDrawerOpen && !messageDrawerClientId,
    staleTime: 300000,
  })

  const filteredComposeClients = React.useMemo(() => {
    if (!clientSearch) return composeClients.slice(0, 20)
    const q = clientSearch.toLowerCase()
    return composeClients.filter((c: any) =>
      c.full_name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q)
    ).slice(0, 20)
  }, [composeClients, clientSearch])

  const { data: notificationsData } = useNotifications({ limit: 10 })
  const unreadNotifications = (notificationsData?.data ?? []).filter((n: any) => !n.isRead)
  const { data: unreadMessages = [] } = useQuery({
    queryKey: ['unread-messages-nav'],
    queryFn: () => api.entities.ClientAdvisorMessage.filter({ is_read: false, sender: 'client' }),
    staleTime: 120000,
    refetchInterval: 120000,
    refetchOnWindowFocus: false,
    enabled: !!user && !isClientUser,
  })

  // Fetch branding settings for white-label support
  const { data: allGlobalSettings = [] } = useQuery({
    queryKey: ['global-settings'],
    queryFn: () => api.entities.GlobalSettings.list(),
    enabled: !!user && !isClientUser,
    staleTime: 600000,
    refetchOnWindowFocus: false,
  })
  const brandingSettings = React.useMemo(
    () => (Array.isArray(allGlobalSettings) ? allGlobalSettings : []).find((s: any) => s.setting_key === 'branding_settings'),
    [allGlobalSettings]
  )

  const navigationGroups = React.useMemo(() => {
    const allGroups = [
      {
        label: 'Ops Center',
        icon: Briefcase,
        isOpen: isOverviewOpen,
        setIsOpen: setIsOverviewOpen,
        items: [
          { title: 'Dashboard', url: '/', icon: LayoutDashboard },
          { title: 'Workload', url: '/workload', icon: CheckSquare, permission: ['tasks', 'view'] },
          { title: 'Calendar', url: '/calendar', icon: CalendarIcon, permission: ['calendar', 'view'] },
          { title: 'Inbox', url: '/inbox', icon: InboxIcon, permission: ['communications', 'view'], badge: unreadNotifications.length || null },
          { title: 'Operations Hub', url: '/operations', icon: Activity, permission: ['workflows', 'view'] },
          { title: 'Tasks', url: '/tasks', icon: CheckSquare, permission: ['tasks', 'view'] },
          { title: 'Cases', url: '/cases', icon: Folder, permission: ['cases', 'view'] },
        ],
      },
      {
        label: 'Client Center',
        icon: UserCircle,
        isOpen: isClientMgmtOpen,
        setIsOpen: setIsClientMgmtOpen,
        items: [
          { title: 'Clients', url: '/clients', icon: Users, permission: ['clients', 'view'] },
          { title: 'Households', url: '/households', icon: Home, permission: ['clients', 'view'] },
          { title: 'Leads', url: '/leads', icon: UserPlus, permission: ['clients', 'view'] },
          { title: 'Actions', url: '/actions', icon: CheckSquare, permission: ['tasks', 'view'] },
          { title: 'Goals', url: '/goals', icon: Target, permission: ['goals', 'view'] },
          { title: 'Reviews', url: '/reviews', icon: FileText, permission: ['reviews', 'view'] },
        ],
      },
      {
        label: 'Comm Center',
        icon: Send,
        isOpen: isEngagementOpen,
        setIsOpen: setIsEngagementOpen,
        items: [
          { title: 'Campaigns', url: '/marketingcampaigns', icon: Share2, permission: ['campaigns', 'view'] },
          { title: 'Forms', url: '/forms', icon: FileText, permission: ['forms', 'view'] },
        ],
      },
      {
        label: 'Tool Center',
        icon: Wrench,
        isOpen: isInsightsOpen,
        setIsOpen: setIsInsightsOpen,
        items: [
          { title: 'Intelligence Center', url: '/intelligencecenter', icon: OliBrandIcon, permission: ['settings', 'intelligence_center'] },
          { title: 'Reports', url: '/reports', icon: BarChart3, permission: ['reports', 'view'] },
          { title: 'Tools', url: '/tools', icon: Settings, permission: ['tools', 'view'] },
          { title: 'Document Signing', url: '/documentsigning', icon: FileText, permission: ['documents', 'view'] },
          { title: 'Compliance Center', url: '/compliancecenter', icon: Shield, permission: ['compliance', 'view'] },
          { title: 'Marketplace', url: '/marketplace', icon: ShoppingBag, permission: ['marketplace', 'view'] },
          { title: 'My Purchases', url: '/mypurchases', icon: CreditCard, permission: ['marketplace', 'view'] },
          { title: 'Analytics', url: '/marketplaceanalytics', icon: TrendingUp, permission: ['reports', 'view'] },
        ],
      },
    ]

    return allGroups
  }, [isOverviewOpen, isClientMgmtOpen, isEngagementOpen, isInsightsOpen, unreadNotifications, unreadMessages])

  if (loading) {
    return (
      <SidebarProvider>
        <div className="min-h-screen flex w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
          <Sidebar
            collapsible="icon"
            className="border-r border-slate-200 bg-white/80 backdrop-blur-sm"
            style={{ '--sidebar-width': '16rem', '--sidebar-width-icon': '4rem' } as React.CSSProperties}
          >
            <SidebarHeader className="p-4">
              <div className="h-8 w-32 bg-slate-200 rounded animate-pulse" />
            </SidebarHeader>
            <SidebarContent>
              <div className="px-3 space-y-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-8 bg-slate-100 rounded animate-pulse" />
                ))}
              </div>
            </SidebarContent>
          </Sidebar>
          <main className="flex-1 flex flex-col overflow-hidden">
            <div className="h-[52px] border-b border-slate-200 bg-white/80 backdrop-blur-sm" />
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-sm text-slate-500">Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    )
  }

  if (!user) {
    return <Navigate to="/login" />
  }

  // Client users get a simplified portal layout (no admin sidebar)
  if (isClientUser) {
    return (
      <>
        <ClientLayout user={user}>
          <Suspense
            fallback={
              <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </ClientLayout>
        <Toaster />
      </>
    )
  }

  return (
    <SidebarProvider>
      <NotificationToastListener userEmail={user?.email} userId={user?.id} />
      <div className="min-h-screen flex w-full bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Sidebar
          collapsible="icon"
          className="border-r border-slate-200 bg-white/80 backdrop-blur-sm"
          style={{ '--sidebar-width': '16rem', '--sidebar-width-icon': '4rem' } as React.CSSProperties}
        >
          <SidebarHeaderContent navigate={navigate} brandingSettings={brandingSettings} />
          <SidebarNav navigationGroups={navigationGroups} location={location} />
          <SidebarFooterContent brandingSettings={brandingSettings} />
        </Sidebar>

        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Spacer to push content below the fixed TopHeader */}
          <div className="h-[52px] shrink-0" />
          <TopHeader
            user={user}
            navigate={navigate}
            unreadNotifications={unreadNotifications}
            userRole={userRole}
            brandingSettings={brandingSettings}
            isCommunicationsOpen={isCommunicationsOpen}
            setIsCommunicationsOpen={setIsCommunicationsOpen}
          />
          <div
            className="flex-1 overflow-auto overflow-x-hidden scrollbar-hide px-2 sm:px-4 md:px-6 py-0"
            id="main-content"
          >
            <Suspense fallback={
              <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            }>
              <Outlet />
            </Suspense>
          </div>
        </main>

        {/* Communications toolbar — fixed, does not scroll */}
        {isCommunicationsOpen && (
          <>
            <div
              className="fixed inset-0 z-30"
              style={{ top: '52px' }}
              onClick={() => setIsCommunicationsOpen(false)}
            />
            <CommunicationsToolbar navigate={navigate} onClose={() => setIsCommunicationsOpen(false)} onCompose={handleCompose} />
          </>
        )}
      </div>

      {user && (
        <>
          <OliAssistant
            isOpen={isOliOpen}
            onToggle={() => setIsOliOpen(!isOliOpen)}
            onMinimize={() => setIsOliMinimized(true)}
            isMinimized={isOliMinimized}
          />
          {(!isOliOpen || isOliMinimized) && (
            <motion.button
              onClick={() => {
                setIsOliOpen(true)
                setIsOliMinimized(false)
              }}
              className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-[#911DFF] to-[#1E60F3] hover:from-[#7a16d9] hover:to-[#1a52d1] text-white rounded-full transition-all z-[49] flex items-center justify-center"
              style={{
                boxShadow:
                  '0 10px 40px rgba(145, 29, 255, 0.4), 0 0 20px rgba(30, 96, 243, 0.3)',
              }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{
                  scale: [1, 1.15, 1],
                  opacity: [0.7, 1, 0.7],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <OliAiIcon className="w-8 h-8" />
              </motion.div>
            </motion.button>
          )}
        </>
      )}

      {/* ── Global Compose Drawers (always mounted) ── */}
      <Suspense fallback={null}>
        <LazyUniversalDrawer
          isOpen={composeOpen}
          onClose={() => { setComposeOpen(false); setComposeClient(null); }}
          title={composeType === 'email' ? 'Compose Email' : 'Compose SMS'}
          icon={composeType === 'email' ? Mail : MessageSquare}
          size="lg"
          showFooter={false}
          noPadding
        >
          <Suspense fallback={<div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}>
            <LazyCommunicationComposer
              communication={null}
              preselectedClient={composeClient}
              defaultType={composeType}
              inDrawer
              onClose={() => { setComposeOpen(false); setComposeClient(null); }}
            />
          </Suspense>
        </LazyUniversalDrawer>
      </Suspense>

      <Suspense fallback={null}>
        <LazyUniversalDrawer
          isOpen={messageDrawerOpen}
          onClose={() => { setMessageDrawerOpen(false); setMessageDrawerClientId(null); setClientSearch(''); }}
          title="New Message"
          icon={PenSquare}
          size="lg"
          showFooter={false}
          noPadding
        >
          {messageDrawerClientId ? (
            <Suspense fallback={<div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>}>
              <LazyMessageThreadDrawer
                clientId={messageDrawerClientId}
                onClose={() => { setMessageDrawerOpen(false); setMessageDrawerClientId(null); }}
                onBack={() => { setMessageDrawerClientId(null); setClientSearch(''); }}
                embedded
              />
            </Suspense>
          ) : (
            <div className="flex flex-col h-full px-5 py-4">
              <div className="pb-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search clients by name or email..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 text-sm border border-slate-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto -mx-1 px-1">
                {filteredComposeClients.length === 0 ? (
                  <div className="text-center py-8">
                    <Users className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">No clients found</p>
                  </div>
                ) : (
                  filteredComposeClients.map((client: any) => (
                    <button
                      key={client.id}
                      onClick={() => setMessageDrawerClientId(client.id)}
                      className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 transition-colors flex items-center gap-3 rounded-lg"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 flex items-center justify-center text-white text-sm font-semibold shrink-0">
                        {client.full_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{client.full_name}</p>
                        <p className="text-xs text-slate-500 truncate">{client.email}</p>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </LazyUniversalDrawer>
      </Suspense>

      <Toaster />
    </SidebarProvider>
  )
}
